import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  Menu,
  X,
  Briefcase,
  Cpu,
  CreditCard,
  Plane,
  Truck,
  Trophy,
  Globe2,
  UserCog,
  Linkedin,
  Mail,
  MapPin,
  Check,
  User,
  TrendingUp,
  GraduationCap,
  Languages,
  Compass,
  Music,
} from "lucide-react";
import { useT } from "@/i18n";
import LanguageSelector from "@/components/LanguageSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "/assets/hero-v2.jpg";
const HERO_IMAGE_MOBILE = "/assets/hero.jpg";
const LOGO = "/assets/logo.png";
const LOGO_WHITE = "/assets/logo-white.png";
const LOGO_HERO = "/assets/logo-hero.png";
const FOUNDER_PHOTO = "/assets/founder-v3.jpg";
const ABOUT_BG = "/assets/mayfair_about-v3.jpg";
const CONTACT_BG = "/assets/albemarle_contact.jpg";
const ABTA_LOGO = "/assets/abta-logo.png";
const EARTH_BG = "/assets/earth_night.jpg";

const EXPERTISE = [
  { icon: Briefcase, key: "commercial", title: "COMMERCIAL & CORPORATE", text: "Sound commercial architecture for complex deals, partnerships and growth." },
  { icon: Cpu, key: "product", title: "PRODUCT & TECHNOLOGY", text: "Legal counsel for technology and AI-driven businesses — from platform launch to scale, including AI governance, regulatory compliance and product liability." },
  { icon: CreditCard, key: "payments", title: "PAYMENTS & FINTECH", text: "Navigating regulation, structuring contracts and entering new markets." },
  { icon: Plane, key: "travel", title: "TRAVEL & HOSPITALITY", text: "Across the full value chain — aviation, cruise, hotels, OTAs and DMCs." },
  { icon: Truck, key: "transport", title: "TRANSPORT & LOGISTICS", text: "Commercial and regulatory advisory for logistics, shipping, freight and supply chain." },
  { icon: Trophy, key: "sports", title: "SPORTS, EVENTS & ENTERTAINMENT", text: "Where commercial stakes are high, timelines are tight and the audience is global." },
  { icon: Globe2, key: "crossborder", title: "CROSS-BORDER & INTERNATIONAL", text: "Multi-jurisdictional expertise built across three continents." },
  { icon: UserCog, key: "fractional", title: "FRACTIONAL GENERAL COUNSEL", text: "Senior legal leadership on a flexible daily or monthly rate — without the full-time overhead." },
];

const COUNTRIES = [
  "United Kingdom", "United States & Canada", "Argentina & LATAM", "Spain",
  "Portugal", "France", "Italy", "Greece & Cyprus",
];

const PILLARS = [
  {
    title: "Built from the inside out",
    text: "Years of in-house experience at American Express Global Business Travel, MSC Group and Hill Dickinson mean we understand what you actually need: clear advice, fast turnaround, and full commercial awareness. No padding. No surprises.",
  },
  {
    title: "Tailored to your specific needs",
    text: "Every engagement is designed around your situation — whether a one-off project, a flexible retainer, or a Fractional General Counsel arrangement. Scope and fees are agreed transparently from day one, with no hidden costs.",
  },
  {
    title: "Creative thinking, legal precision",
    text: "The best legal work, like the best music, is built on mastery, creativity and the ability to find solutions where others hear only noise. Complex problems demand independent thinking — that is what we bring to every engagement.",
  },
];

/* ------------------------------ Nav ------------------------------ */
function Nav() {
  const { t } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll + handle Escape while menu is open
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = () => setOpen(false);

  const NAV_LINKS = [
    { label: t("nav.about"), href: "#about", key: "about" },
    { label: t("nav.expertise"), href: "#expertise", key: "expertise" },
    { label: t("nav.founder"), href: "#founder", key: "founder" },
    { label: t("nav.international"), href: "#international", key: "international" },
    { label: t("nav.contact"), href: "#footer", key: "contact" },
  ];

  return (
    <>
      <header className={`sm-nav-bar ${scrolled ? "is-scrolled" : ""}`} data-testid="nav-bar">
        <div className="sm-nav-bar__inner">
          <a href="#top" className="sm-nav-bar__logo" data-testid="nav-logo">
            <img src={scrolled ? LOGO_WHITE : LOGO_HERO} alt="Santa Maria Legal Advisory" />
          </a>
          <nav className="sm-nav-bar__links" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <a key={l.key} href={l.href} data-testid={`nav-${l.key}`}>{l.label}</a>
            ))}
          </nav>
          <div className="sm-nav-bar__right">
            <LanguageSelector variant="desktop" />
            <a href="#contact" className="sm-btn sm-btn--gold sm-nav-bar__cta" data-testid="nav-cta">
              {t("nav.cta")} <ArrowRight size={14} strokeWidth={2} />
            </a>
          </div>
          <button
            className="sm-nav-bar__burger"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            data-testid="nav-burger"
          >
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {open && (
        <div
          className="sm-mobile-menu"
          role="dialog"
          aria-modal="true"
          data-testid="mobile-menu"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <button className="sm-mobile-menu__close" onClick={close} aria-label="Close menu" data-testid="mobile-menu-close">
            <X size={28} strokeWidth={1.5} />
          </button>
          <div className="sm-mobile-menu__inner">
            <LanguageSelector variant="mobile" />
            {NAV_LINKS.map((l) => (
              <a key={l.key} href={l.href} onClick={close} data-testid={`m-nav-${l.key}`}>
                {l.label}
              </a>
            ))}
            <a href="#contact" onClick={close} className="sm-btn sm-btn--gold">
              {t("nav.cta")} <ArrowRight size={14} strokeWidth={2} />
            </a>
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------ Hero ------------------------------ */
function Hero() {
  const { t } = useT();
  return (
    <section id="top" className="sm-hero" data-testid="hero" style={{ "--sm-hero-mobile-bg": `url(${HERO_IMAGE_MOBILE})` }}>
      <img src={HERO_IMAGE} alt="" className="sm-hero__img" aria-hidden="true" />
      <div className="sm-hero__overlay" />
      <div className="sm-hero__content">
        <div className="sm-eyebrow sm-eyebrow--gold">{t("hero.eyebrow")}</div>
        <h1 className="sm-hero__title">{t("hero.title_l1")}<br />{t("hero.title_l2")}</h1>
        <p className="sm-hero__sub">{t("hero.sub")}</p>
        <div className="sm-hero__ctas">
          <a href="#expertise" className="sm-btn sm-btn--outline" data-testid="hero-cta-expertise">{t("hero.cta_expertise")}</a>
          <a href="#contact" className="sm-btn sm-btn--gold" data-testid="hero-cta-contact">{t("hero.cta_contact")} <ArrowRight size={14} strokeWidth={2} /></a>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Ornament ------------------------------ */
const Divider = () => (
  <div className="sm-divider" aria-hidden="true">
    <span className="sm-divider__line" />
    <span className="sm-divider__diamond" />
    <span className="sm-divider__line" />
  </div>
);

/* ------------------------------ About ------------------------------ */
function About() {
  const { t } = useT();
  return (
    <section id="about" className="sm-section sm-about" data-testid="about">
      <div
        className="sm-about__hero"
        style={{ backgroundImage: `url(${ABOUT_BG})` }}
        aria-hidden="true"
      >
        <div className="sm-about__hero-fade" />
      </div>
      <div className="sm-container sm-about__body">
        <div className="sm-eyebrow">{t("about.eyebrow")}</div>
        <h2 className="sm-h2 sm-about__lead-h2">An unpredictable world calls for trusted, experienced legal advice that understands your business, anticipates your risks, and turns complexity into decisive action.</h2>
        <div className="sm-prose">
          <p className="sm-about__lead"><strong>Santa Maria Legal Advisory</strong> is a London-based specialist firm providing senior-level commercial, regulatory and cross-border legal advisory services to businesses across the UK, Europe and the Americas.</p>
          <p>We operate on a flexible advisory model — acting as a trusted strategic partner rather than a traditional law firm, with fee structures designed to give clients access to high-calibre expertise without the overhead of a full-time hire.</p>
          <p>Our approach is particularly suited to fast-growing businesses, scale-ups, international companies entering new markets, and established organisations requiring specialist expertise on a project or retainer basis.</p>
          <p><strong>For us, the relationship always comes first.</strong></p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Pillars ------------------------------ */
function Pillars() {
  const { t } = useT();
  return (
    <section className="sm-section sm-section--white" data-testid="pillars">
      <div className="sm-container">
        <div className="sm-eyebrow sm-eyebrow--center">{t("pillars.eyebrow")}</div>
        <h2 className="sm-h2 sm-h2--center sm-pillars__title">
          Three reasons why clients{" "}
          <span className="sm-pillars__title-break">choose us</span>
        </h2>
        <div className="sm-pillars">
          {PILLARS.map((p) => (
            <article key={p.title} className="sm-pillar" data-testid={`pillar-${p.title.split(" ")[0].toLowerCase()}`}>
              <h3>{p.title}</h3>
              <p>{p.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Expertise ------------------------------ */
function Expertise() {
  const { t } = useT();
  return (
    <section id="expertise" className="sm-section sm-section--cream" data-testid="expertise">
      <div className="sm-container">
        <div className="sm-eyebrow sm-eyebrow--center">{t("expertise.eyebrow")}</div>
        <h2 className="sm-h2 sm-h2--center">{t("expertise.headline")}</h2>
        <div className="sm-expertise">
          {EXPERTISE.map((e) => {
            const Icon = e.icon;
            return (
              <article key={e.key} className="sm-expertise__card" data-testid={`expertise-${e.key}`}>
                <div className="sm-expertise__icon"><Icon size={32} strokeWidth={1.25} /></div>
                <h3>{e.title}</h3>
                <p>{e.text}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ ABTA ------------------------------ */
function Abta() {
  return (
    <section className="sm-section sm-section--white sm-abta" data-testid="abta">
      <div className="sm-container sm-container--narrow">
        <div className="sm-abta__logo" data-testid="abta-logo">
          <div className="sm-abta__logo-frame">
            <img src={ABTA_LOGO} alt="ABTA Partner — Business Services Network" />
          </div>
        </div>
        <p className="sm-abta__lead">
          Santa Maria Legal Advisory is a proud member of<br />
          ABTA&rsquo;s Legal Partner network.
        </p>
        <p className="sm-abta__body">
          We advise travel and hospitality businesses on regulatory compliance, commercial contracts,
          technology adoption and AI governance — across the full value chain. As ABTA Legal Partners and regular
          contributors to industry forums, we bring specialist expertise in Package Travel Regulations, consumer
          protection, distribution agreements, OTA and DMC contracts, and the evolving regulatory landscape around
          AI and technology in travel.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------ International ------------------------------ */
function International() {
  const { t } = useT();
  return (
    <section id="international" className="sm-section sm-section--navy sm-international" data-testid="international">
      <div
        className="sm-international__bg"
        style={{ backgroundImage: `url(${EARTH_BG})` }}
        aria-hidden="true"
      />
      <div className="sm-container">
        <h2 className="sm-h2 sm-h2--center sm-h2--gold">{t("international.headline")}</h2>
        <p className="sm-section__lead sm-section__lead--light">{t("international.lead")}</p>
        <div className="sm-countries">
          {COUNTRIES.map((c) => (
            <div key={c} className="sm-country" data-testid={`country-${c.replace(/[^a-z]/gi, "").toLowerCase()}`}>
              <MapPin size={14} strokeWidth={1.8} />
              <span>{c}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Founder ------------------------------ */
function Founder() {
  const { t } = useT();
  const BLOCKS = [
    { icon: User, title: "18+ YEARS OF EXPERIENCE", desc: "Senior legal leadership across Europe, the Americas and international markets." },
    { icon: Globe2, title: "CROSS-BORDER EXPERTISE", desc: "Advising businesses and senior stakeholders across the globe." },
    { icon: TrendingUp, title: "$800M+ COMMERCIAL VALUE SUPPORTED", desc: "Supporting major commercial transactions and strategic initiatives." },
    { icon: Briefcase, title: "FORMER ROLES", desc: "American Express GBT · Hill Dickinson LLP · Mediterranean Shipping Company Group · Brödermann Jahn · Volkswagen" },
    { icon: GraduationCap, title: "ACADEMIC CREDENTIALS", desc: "Exec MBA, Warwick Business School · LLM, Queen Mary London (Chevening Scholar) · MSc. Economic Business Law" },
    { icon: Languages, title: "FIVE LANGUAGES", desc: "English, Spanish, French, Italian and Portuguese." },
    { icon: Compass, title: "SECTOR EXPERIENCE", desc: "Technology, Payments, Travel, Hospitality, Transport and Logistics." },
    { icon: Music, title: "BEYOND THE LAW", desc: "Guitarist, pianist, singer and songwriter. Bringing creativity and independent thinking to complex challenges." },
  ];
  return (
    <section id="founder" className="sm-founder-v2" data-testid="founder">
      <div className="sm-founder-v2__photo" data-testid="founder-photo">
        <img src={FOUNDER_PHOTO} alt="Ezequiel T. Condoluci Santa Maria, Founder & Head of Advisory" />
        <div className="sm-founder-v2__photo-eyebrow">{t("founder.eyebrow")}</div>
        <div className="sm-founder-v2__photo-fade" />
      </div>
      <div className="sm-founder-v2__overlay" aria-hidden="true">
        <div className="sm-founder-v2__overlay-eyebrow">{t("founder.eyebrow")}</div>
        <div className="sm-founder-v2__overlay-name">Ezequiel T. Condoluci Santa Maria</div>
        <div className="sm-founder-v2__overlay-role">Founder &amp; Head of Advisory</div>
        <div className="sm-founder-v2__overlay-cred sm-founder-v2__overlay-cred--bold">Solicitor (England &amp; Wales) · SRA No. 653063</div>
        <div className="sm-founder-v2__overlay-cred sm-founder-v2__overlay-cred--bold">Abogado (Argentina) · CPACF T.105 F.495</div>
        <div className="sm-founder-v2__overlay-cred">Executive MBA, Warwick Business School (UK)</div>
        <div className="sm-founder-v2__overlay-cred">LLM, Queen Mary London (UK, Chevening Scholar)</div>
        <div className="sm-founder-v2__overlay-cred">MSc. Economic Business Law, UCA &amp; Bucerius Law School (Argentina &amp; Germany)</div>
      </div>
      <div className="sm-container sm-founder-v2__body">
        <h2 className="sm-h2">Ezequiel T. Condoluci Santa Maria</h2>
        <p className="sm-founder-v2__role">{t("founder.title")}</p>
        <p className="sm-founder-v2__credentials">
          <span className="sm-founder-v2__cred-line sm-founder-v2__cred-line--flag">Solicitor (England &amp; Wales) · SRA No. 653063</span>
          <span className="sm-founder-v2__cred-line sm-founder-v2__cred-line--flag">Abogado (Argentina) · CPACF T.105 F.495</span>
          <span className="sm-founder-v2__cred-line sm-founder-v2__cred-line--academic">Executive MBA, Warwick Business School (UK)</span>
          <span className="sm-founder-v2__cred-line sm-founder-v2__cred-line--academic">LLM, Queen Mary London (UK, Chevening Scholar)</span>
          <span className="sm-founder-v2__cred-line sm-founder-v2__cred-line--academic">MSc. Economic Business Law, UCA &amp; Bucerius Law School (Argentina &amp; Germany)</span>
        </p>
        <div className="sm-founder-v2__bio">
          <p>{t("founder.p1")}</p>
          <p>{t("founder.p2")}</p>
          <p>{t("founder.p3")}</p>
          <p>{t("founder.p4")}</p>
          <p>{t("founder.p5")}</p>
          <p>{t("founder.p6")}</p>
        </div>

        <div className="sm-stats8" data-testid="stats-grid">
          {BLOCKS.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.title} className="sm-stat-card" data-testid={`stat-${b.title.split(" ")[0].toLowerCase()}`}>
                <div className="sm-stat-card__icon"><Icon size={28} strokeWidth={1.4} /></div>
                <div className="sm-stat-card__title">{b.title}</div>
                <div className="sm-stat-card__divider" aria-hidden="true" />
                <p className="sm-stat-card__desc">{b.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Contact ------------------------------ */
function Contact() {
  const { t } = useT();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", enquiry: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.enquiry) {
      toast.error(t("contact.err_required"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ first_name: "", last_name: "", email: "", phone: "", enquiry: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || t("contact.err_delivery");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const sendAnother = () => setSubmitted(false);

  return (
    <section id="contact" className="sm-section sm-section--contact-bg sm-cta" data-testid="contact">
      <div
        className="sm-contact-bg"
        style={{ backgroundImage: `url(${CONTACT_BG})` }}
        aria-hidden="true"
      />
      <div className="sm-container">
        <div className="sm-contact-layout">
          <aside
            className="sm-contact-side"
            aria-hidden="true"
            data-testid="contact-side"
            style={{ backgroundImage: `url(${CONTACT_BG})` }}
          />
          <div className="sm-cta-form-col">
            <h2 className="sm-h2">{t("contact.headline")}</h2>
            <p className="sm-section__lead">{t("contact.lead")}</p>

        {submitted ? (
          <div className="sm-confirm" role="status" aria-live="polite" data-testid="contact-confirmation">
            <div className="sm-confirm__icon" aria-hidden="true">
              <Check size={26} strokeWidth={2} />
            </div>
            <div className="sm-confirm__title">{t("contact.confirm_title")}</div>
            <p className="sm-confirm__body">{t("contact.confirm_body")}</p>
            <p className="sm-confirm__sub">{t("contact.confirm_sub")}</p>
            <button
              type="button"
              className="sm-confirm__again"
              onClick={sendAnother}
              data-testid="contact-send-another"
            >
              {t("contact.send_another")}
            </button>
          </div>
        ) : (
          <form className="sm-form" onSubmit={submit} data-testid="contact-form" noValidate>
            <div className="sm-form__row">
              <div className="sm-field">
                <label htmlFor="first_name">{t("contact.first_name")}</label>
                <input id="first_name" name="first_name" type="text" value={form.first_name} onChange={onChange} required data-testid="input-first-name" />
              </div>
              <div className="sm-field">
                <label htmlFor="last_name">{t("contact.last_name")}</label>
                <input id="last_name" name="last_name" type="text" value={form.last_name} onChange={onChange} required data-testid="input-last-name" />
              </div>
            </div>
            <div className="sm-form__row">
              <div className="sm-field">
                <label htmlFor="email">{t("contact.email")}</label>
                <input id="email" name="email" type="email" value={form.email} onChange={onChange} required data-testid="input-email" />
              </div>
              <div className="sm-field">
                <label htmlFor="phone">{t("contact.phone")}</label>
                <input id="phone" name="phone" type="tel" value={form.phone} onChange={onChange} required data-testid="input-phone" />
              </div>
            </div>
            <div className="sm-field">
              <label htmlFor="enquiry">{t("contact.enquiry")}</label>
              <textarea id="enquiry" name="enquiry" value={form.enquiry} onChange={onChange} required rows={5} data-testid="input-enquiry" />
            </div>
            <button type="submit" className="sm-btn sm-btn--gold sm-btn--lg" disabled={submitting} data-testid="submit-contact">
              {submitting ? t("contact.sending") : t("contact.submit")} {!submitting && <ArrowRight size={14} strokeWidth={2} />}
            </button>
            <p className="sm-form__legal">
              {t("contact.legal_pre")}
              <Link to="/privacy" data-testid="form-privacy-link">{t("contact.legal_link")}</Link>
              {t("contact.legal_post")}
            </p>
          </form>
        )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Footer ------------------------------ */
function Footer() {
  const { t } = useT();
  return (
    <footer id="footer" className="sm-footer" data-testid="footer">
      <div className="sm-footer__main">
        <div className="sm-footer__col sm-footer__brand">
          <img src={LOGO_WHITE} alt="Santa Maria Legal Advisory" className="sm-footer__logo" />
          <div className="sm-footer__brandname">SANTA MARIA LEGAL ADVISORY LTD</div>
          <div className="sm-footer__addr">3rd Floor, 45 Albemarle Street<br />Mayfair, London W1S 4JL</div>
          <a href="mailto:info@smlegal.co.uk" className="sm-footer__link" data-testid="footer-email">
            <Mail size={13} strokeWidth={1.8} /> info@smlegal.co.uk
          </a>
        </div>

        <div className="sm-footer__col">
          <div className="sm-footer__heading">{t("footer.navigate")}</div>
          <a href="#about">{t("nav.about")}</a>
          <a href="#expertise">{t("nav.expertise")}</a>
          <a href="#international">{t("nav.international")}</a>
          <a href="#contact">{t("nav.contact")}</a>
          <Link to="/privacy" data-testid="footer-privacy">{t("footer.privacy")}</Link>
        </div>

        <div className="sm-footer__col">
          <div className="sm-footer__heading">{t("footer.credentials")}</div>
          <span>Company No. 17187611 (England &amp; Wales)</span>
          <span>ABTA Legal Partner</span>
          <a
            href="https://www.linkedin.com/company/santa-maria-legal-advisory/"
            target="_blank"
            rel="noopener noreferrer"
            className="sm-footer__social"
            aria-label="LinkedIn"
            data-testid="footer-linkedin"
          >
            <Linkedin size={16} strokeWidth={1.6} />
          </a>
        </div>
      </div>

      <div className="sm-footer__legal">{t("footer.legal")}</div>

      <div className="sm-footer__bottom">
        © {new Date().getFullYear()} Santa Maria Legal Advisory Ltd · {t("footer.rights")}
      </div>
    </footer>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Nav />
      <Hero />
      <About />
      <Pillars />
      <Expertise />
      <Abta />
      <Founder />
      <International />
      <Contact />
      <Footer />
    </main>
  );
}

